import React, { useState } from 'react';
import { cn } from '@/lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  onChange?: (id: string) => void;
  className?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Tabs({ tabs, defaultTab, onChange, className }: TabsProps) {
  const [activeId, setActiveId] = useState<string>(defaultTab ?? tabs[0]?.id ?? '');

  function handleSelect(id: string) {
    setActiveId(id);
    onChange?.(id);
  }

  const activeTab = tabs.find((t) => t.id === activeId);

  return (
    <div className={cn('w-full flex flex-col', className)}>
      {/* Tab bar */}
      <div
        role="tablist"
        aria-label="Tabs"
        className="flex border-b border-slate-200 overflow-x-auto scrollbar-hide flex-shrink-0 px-5"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.id}`}
              id={`tab-${tab.id}`}
              type="button"
              disabled={tab.disabled}
              onClick={() => handleSelect(tab.id)}
              className={cn(
                'relative shrink-0 px-4 py-2.5 text-sm font-medium whitespace-nowrap inline-flex items-center gap-1.5',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset',
                'transition-colors duration-150',
                'disabled:cursor-not-allowed disabled:opacity-40',
                isActive
                  ? 'text-blue-600 border-b-2 border-blue-600 -mb-px'
                  : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent -mb-px',
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab panel */}
      {activeTab && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeTab.id}`}
          aria-labelledby={`tab-${activeTab.id}`}
          className="flex-1 min-h-0 flex flex-col"
        >
          {activeTab.content}
        </div>
      )}
    </div>
  );
}
